import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// Shared chip primitive. Two shapes:
//
//   <Chip tone="…">…</Chip>          — read-only chip rendered as a <span>
//   <ToggleChip selected={…}>…</…>   — click-to-toggle chip rendered as a <button>
//
// Both consume the same `cva` config so the visual language stays in lock
// step. A `tone` token resolves to one of the design system's role colors
// (success / destructive / warning / info / neutral / muted / outline).
// `<ChipLabel>` is the uppercase mini-prefix used by data chips.

const chipVariants = cva(
  'inline-flex select-none items-center gap-1.5 rounded-full border font-medium transition-all duration-150 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted/50 text-foreground',
        info: 'border-primary/20 bg-primary/10 text-primary',
        success: 'border-success/30 bg-success/15 text-success',
        destructive: 'border-destructive/30 bg-destructive/10 text-destructive',
        warning: 'border-warning/50 bg-warning/15 text-warning-foreground',
        muted: 'border-border/60 bg-transparent text-muted-foreground',
        outline: 'border-border bg-transparent text-foreground',
      },
      size: {
        sm: 'h-6 px-2 text-[11px] [&_svg]:size-3',
        default: 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
        lg: 'h-9 px-3 text-sm [&_svg]:size-4',
      },
      interactive: {
        true: 'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:brightness-95 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60',
        false: '',
      },
    },
    defaultVariants: {
      tone: 'neutral',
      size: 'default',
      interactive: false,
    },
  },
);

export type ChipTone = NonNullable<VariantProps<typeof chipVariants>['tone']>;
export type ChipSize = NonNullable<VariantProps<typeof chipVariants>['size']>;

interface ChipProps
  extends
    Omit<React.ComponentProps<'span'>, 'children'>,
    Omit<VariantProps<typeof chipVariants>, 'interactive'> {
  asChild?: boolean;
  children?: React.ReactNode;
}

export function Chip({ className, tone, size, asChild = false, ...props }: Readonly<ChipProps>) {
  const Comp = asChild ? Slot : 'span';
  return (
    <Comp
      data-slot="chip"
      className={cn(chipVariants({ tone, size, interactive: false }), className)}
      {...props}
    />
  );
}

type ChipLabelProps = React.ComponentProps<'span'>;

export function ChipLabel({ className, ...props }: Readonly<ChipLabelProps>) {
  return (
    <span
      data-slot="chip-label"
      className={cn('text-[10px] font-semibold uppercase tracking-wider opacity-70', className)}
      {...props}
    />
  );
}

interface ToggleChipProps
  extends
    Omit<React.ComponentProps<'button'>, 'children'>,
    Omit<VariantProps<typeof chipVariants>, 'interactive' | 'tone'> {
  selected: boolean;
  /** Tone shown when `selected` is true. Defaults to `success`. */
  selectedTone?: ChipTone;
  /** Tone shown when `selected` is false. Defaults to `outline`. */
  unselectedTone?: ChipTone;
  children?: React.ReactNode;
}

export function ToggleChip({
  className,
  selected,
  selectedTone = 'success',
  unselectedTone = 'outline',
  size,
  type = 'button',
  ...props
}: Readonly<ToggleChipProps>) {
  return (
    <button
      type={type}
      data-slot="chip"
      data-state={selected ? 'selected' : 'unselected'}
      aria-pressed={selected}
      className={cn(
        chipVariants({
          tone: selected ? selectedTone : unselectedTone,
          size,
          interactive: true,
        }),
        className,
      )}
      {...props}
    />
  );
}

export { chipVariants };
